/**
 * Résolution de la cible unique des outils de forme (spec #1858) contre ce
 * qu'elle vise dans l'acte lu (`ActeVise`) : cible → paragraphes dans l'ordre,
 * identifiants non résolus, paragraphes couverts mais hors de portée, et
 * cellules de tableau laissées à leur tableau.
 *
 * WHY un module à part : la lecture de forme (`formePourLAgent.ts`) n'est que
 * le premier consommateur. Les outils d'écriture de forme visent la même cible
 * et doivent la résoudre de la même façon — une seconde résolution finirait par
 * lire un paragraphe et en écrire un autre.
 *
 * Module pur : il ne connaît ni l'éditeur ni le navigateur. Il ne résout que
 * les cibles de PARAGRAPHES ; le passage de texte (`kind:"text"`) appartient à
 * l'outil qui règle du texte.
 */

import type {
  CanvasFormatSkippedReason,
  CanvasFormatUnresolvedReason,
  CanvasParagraphTarget,
} from '@hector/shared-types';
import type { ActeVise, ParagrapheVise } from '@hector/docx-forme';

/** Un paragraphe visé que l'agent peut nommer : le fichier lui donne un identifiant, que nul autre ne porte. */
export type ParagrapheIdentifie = ParagrapheVise & { nodeId: string };

/**
 * Un paragraphe désigné par une cible, avec son rang parmi les paragraphes
 * visés (`ActeVise.paragraphes`, à partir de 0, ordre du fichier). Une liste est
 * rendue dans l'ordre demandé : le rang est ce qui permet à un outil d'écriture
 * de la remettre dans l'ordre du document.
 */
export type ParagrapheCible = ParagrapheIdentifie & { rang: number };

/**
 * Ce qu'une cible « tout l'acte » ou une plage fait des cellules de tableau du
 * corps, selon ce que l'appelant va écrire ou lire :
 * - `cellules_couvertes` — une forme de TEXTE, une lecture : une police ou une
 *   taille « sur tout l'acte » vaut aussi dans les tableaux ;
 * - `cellules_par_identifiants` — une forme de PARAGRAPHE (alignement, retraits,
 *   espacement, bordure, style, remise à zéro, copie de forme) : la mise en
 *   page d'une cellule appartient à son tableau. « Justifie tout l'acte » ne
 *   justifie ni un titre encadré, qui vit dans un tableau, ni une colonne de
 *   montants. Ces cellules sont laissées et COMPTÉES ; on les atteint en les
 *   nommant (`ids`), ou par une plage qui ne couvre QUE des cellules — elle
 *   vise alors le tableau.
 */
export type PorteeDansLesTableaux = 'cellules_couvertes' | 'cellules_par_identifiants';

export interface CibleDeParagraphesResolue {
  /** Les paragraphes désignés et lisibles, dans l'ordre où ils sont rendus. */
  paragraphes: ParagrapheCible[];
  /** Identifiants demandés qui ne désignent aucun paragraphe visé, avec la raison. */
  nonResolus: Array<{ nodeId: string; reason: CanvasFormatUnresolvedReason }>;
  /**
   * Paragraphes que la cible COUVRE sans pouvoir les rendre, comptés par
   * raison. Une plage ou « tout l'acte » ne nomme pas ses paragraphes : ceux
   * qu'on écarte n'ont pas d'identifiant à citer, seulement un nombre.
   */
  omis: Array<{ reason: CanvasFormatSkippedReason; count: number }>;
  /**
   * Les cellules de tableau du corps que la cible touche : `rendues`, les
   * identifiants de celles qui sont parmi `paragraphes`, dans leur ordre ;
   * `laissees`, le nombre de celles qu'une cible « tout » ou une plage couvre
   * et laisse à leur tableau ({@link PorteeDansLesTableaux}). Ni hors de
   * portée ni « omises » : un autre genre de cible les atteint.
   */
  cellules: { rendues: string[]; laissees: number };
}

/** Ce qu'une tranche couvre sans le rendre, par raison. Un identifiant en double n'est pas une absence d'identifiant au fichier. */
function compterLesOmis(sansIdentifiant: number, enDouble: number, enTableau: number): CibleDeParagraphesResolue['omis'] {
  return [
    ...(enTableau === 0 ? [] : [{ reason: 'table_cell' as const, count: enTableau }]),
    ...(sansIdentifiant === 0 ? [] : [{ reason: 'no_node_id' as const, count: sansIdentifiant }]),
    ...(enDouble === 0 ? [] : [{ reason: 'duplicate_node_id' as const, count: enDouble }]),
  ];
}

/**
 * Résout une cible de paragraphes contre ce qu'elle vise dans l'acte lu.
 *
 * Une liste est rendue dans l'ordre DEMANDÉ (l'appelant relie chaque résultat à
 * sa demande), une plage et tout l'acte dans l'ordre du document. Une plage
 * dont une borne manque ne rend rien : choisir une autre borne à sa place
 * ferait lire — ou écrire — un passage que l'agent n'a pas désigné.
 *
 * SEUL endroit qui lit `enCellule` pour décider de ce qui s'écrit.
 *
 * @param acte Ce que la cible vise dans le fichier exporté par l'éditeur : le corps avec ses cellules, ou un lieu.
 * @param portee Ce que « tout » et une plage font des cellules de tableau du corps.
 * @param cible La cible demandée par l'agent.
 */
export function resoudreLaCibleDeParagraphes(acte: ActeVise, portee: PorteeDansLesTableaux, cible: CanvasParagraphTarget): CibleDeParagraphesResolue {
  /** Le paragraphe de rang donné, s'il porte un identifiant. */
  const auRang = (rang: number): ParagrapheCible[] => {
    const lu = acte.paragraphes[rang];
    return lu === undefined || lu.nodeId === null ? [] : [{ ...lu, nodeId: lu.nodeId, rang }];
  };
  const cellulesParmi = (paragraphes: readonly ParagrapheCible[]): string[] => paragraphes.filter((lu) => lu.enCellule).map((lu) => lu.nodeId);

  // Tranche [debut, fin] des paragraphes visés, bornes incluses : rend les
  // lisibles, compte ce qu'elle couvre sans le rendre, et laisse à leur tableau
  // les cellules qu'une forme de paragraphe ne doit pas écrire.
  const tranche = (debut: number, fin: number, enTableauDuLieu: number, viseLeTableauSiElleNeCouvreQueLui: boolean): Omit<CibleDeParagraphesResolue, 'nonResolus'> => {
    const couverts = Array.from({ length: Math.max(0, fin - debut + 1) }, (_, decalage) => debut + decalage);
    // Une PLAGE qui ne couvre QUE des cellules vise le tableau : elles sont écrites comme si elles étaient nommées.
    const laisse = portee === 'cellules_par_identifiants'
      && !(viseLeTableauSiElleNeCouvreQueLui && couverts.every((rang) => acte.paragraphes[rang]?.enCellule === true));
    const rangs = laisse ? couverts.filter((rang) => acte.paragraphes[rang]?.enCellule === false) : couverts;
    const paragraphes = rangs.flatMap(auRang);
    const enDouble = rangs.filter((rang) => acte.paragraphes[rang]?.identifiantEnDouble !== undefined).length;
    return {
      paragraphes,
      omis: compterLesOmis(rangs.length - paragraphes.length - enDouble, enDouble, enTableauDuLieu),
      cellules: { rendues: cellulesParmi(paragraphes), laissees: couverts.length - rangs.length },
    };
  };

  if (cible.kind === 'all') {
    return { ...tranche(0, acte.paragraphes.length - 1, acte.cellulesNonVisees.length, false), nonResolus: [] };
  }

  const rangParId = new Map(acte.paragraphes.flatMap((lu, rang) => (lu.nodeId === null ? [] : [[lu.nodeId, rang] as const])));
  const idsEnTableau = new Set(acte.cellulesNonVisees.map((cellule) => cellule.nodeId));
  const nonResolus: CibleDeParagraphesResolue['nonResolus'] = [];
  const idsEnDouble = new Set(acte.paragraphes.flatMap((lu) => (lu.identifiantEnDouble === undefined ? [] : [lu.identifiantEnDouble])));
  const rangDe = (nodeId: string): number | undefined => {
    const rang = rangParId.get(nodeId);
    // Un identifiant en double n'est dit tel que si AUCUN de ses porteurs ne le garde (le corps) ; dans un lieu, le premier le garde et se résout.
    if (rang === undefined) nonResolus.push({ nodeId, reason: idsEnTableau.has(nodeId) ? 'table_cell' : idsEnDouble.has(nodeId) ? 'duplicate_node_id' : 'not_found' });
    return rang;
  };

  if (cible.kind === 'ids') {
    const paragraphes = [...new Set(cible.nodeIds)].map(rangDe).filter((rang): rang is number => rang !== undefined).flatMap(auRang);
    // Une liste nomme chaque paragraphe : ce qu'elle ne rend pas est non résolu, jamais « omis » — et une cellule nommée est rendue.
    return { paragraphes, nonResolus, omis: [], cellules: { rendues: cellulesParmi(paragraphes), laissees: 0 } };
  }

  const rangs = [...new Set([cible.from, cible.to])].map(rangDe).filter((rang): rang is number => rang !== undefined);
  if (nonResolus.length > 0) return { paragraphes: [], nonResolus, omis: [], cellules: { rendues: [], laissees: 0 } };
  const [debut, fin] = [Math.min(...rangs), Math.max(...rangs)];
  // Dans un lieu, un paragraphe de tableau de rang r se trouve entre les paragraphes r - 1 et
  // r : la plage le couvre s'il est strictement après sa première borne.
  const enTableauDuLieu = acte.cellulesNonVisees.filter((cellule) => cellule.rang > debut && cellule.rang <= fin).length;
  return { ...tranche(debut, fin, enTableauDuLieu, true), nonResolus };
}
